/**
 * The phone's loop clock, as a pure function of time.
 *
 * `useLoopClock` in packages/ui/src/internal/motion-runtime.ts runs a shared
 * value 0 to 1 forever: `withRepeat(withTiming(1, { duration, easing }), -1,
 * reverse)`. A reversing loop spends HALF its period per leg, so `period` is
 * always the full visible round trip, and it eases on `breathe`; a wrapping
 * loop spends the whole period and eases on `linear`. The web draws each frame
 * from the time since the loop started, so this answers the value that clock
 * holds at that time. The curves are the tokens' own four control points.
 *
 * `interpolate` is Reanimated's, clamped at both ends, which is how every
 * drawing here reads its window off the clock.
 *
 * Pure: `motion.test.ts` holds it.
 */
import type { EasingName } from './tokens';
import { EASING_POINTS } from './tokens';

/**
 * A CSS `cubic-bezier(x1, y1, x2, y2)` as a function of progress, 0..1.
 * Newton's method, with bisection when the slope is too flat to trust.
 */
export const cubicBezier = (x1: number, y1: number, x2: number, y2: number): ((u: number) => number) => {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number): number => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number): number => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number): number => (3 * ax * t + 2 * bx) * t + cx;

  const solve = (x: number): number => {
    let t = x;
    for (let i = 0; i < 8; i += 1) {
      const error = sampleX(t) - x;
      if (Math.abs(error) < 1e-6) return t;
      const slope = slopeX(t);
      if (Math.abs(slope) < 1e-6) break;
      t -= error / slope;
    }
    let low = 0;
    let high = 1;
    t = x;
    for (let i = 0; i < 40 && low < high; i += 1) {
      const value = sampleX(t);
      if (Math.abs(value - x) < 1e-6) return t;
      if (x > value) low = t;
      else high = t;
      t = (low + high) / 2;
    }
    return t;
  };

  return (u: number): number => {
    if (u <= 0) return 0;
    if (u >= 1) return 1;
    return sampleY(solve(u));
  };
};

const curves = new Map<EasingName, (u: number) => number>();

/** A motion token's curve. Built once per token. */
export const easing = (name: EasingName): ((u: number) => number) => {
  const known = curves.get(name);
  if (known !== undefined) return known;
  const [x1, y1, x2, y2] = EASING_POINTS[name];
  const curve = cubicBezier(x1, y1, x2, y2);
  curves.set(name, curve);
  return curve;
};

export type LoopSpec = {
  /** The full visible round trip, in milliseconds: the drawing's own `PERIOD_MS`. */
  readonly period: number;
  /** `true` runs out and back, as a breath does; `false` runs 0 to 1 and wraps. */
  readonly reverse?: boolean;
  /** Default `breathe` when it reverses, `linear` when it wraps: the phone's defaults. */
  readonly easing?: EasingName;
};

/** The phone's `useLoopClock` value, `time` milliseconds after the loop started. */
export const loopValue = (time: number, { period, reverse = false, easing: name }: LoopSpec): number => {
  if (period <= 0 || !Number.isFinite(time) || time <= 0) return 0;
  const curve = easing(name ?? (reverse ? 'breathe' : 'linear'));
  if (!reverse) return curve((time % period) / period);
  const leg = period / 2;
  const legs = Math.floor(time / leg);
  const u = (time - legs * leg) / leg;
  // Reanimated swaps the two ends on every repeat and runs the same curve
  // forward in time, so the way back is 1 − curve(u), not curve(1 − u).
  return legs % 2 === 0 ? curve(u) : 1 - curve(u);
};

/**
 * Reanimated's `interpolate(value, input, output, Extrapolation.CLAMP)`.
 * `input` must be increasing and the two lists the same length.
 */
export const interpolate = (value: number, input: readonly number[], output: readonly number[]): number => {
  const count = Math.min(input.length, output.length);
  if (count === 0) return 0;
  const firstIn = input[0] ?? 0;
  const firstOut = output[0] ?? 0;
  if (count === 1 || value <= firstIn) return firstOut;
  for (let i = 1; i < count; i += 1) {
    const x1 = input[i] ?? 0;
    if (value <= x1) {
      const x0 = input[i - 1] ?? 0;
      const y0 = output[i - 1] ?? 0;
      const y1 = output[i] ?? 0;
      return x1 === x0 ? y1 : y0 + ((value - x0) / (x1 - x0)) * (y1 - y0);
    }
  }
  return output[count - 1] ?? 0;
};
